import { useState, useRef, useEffect, RefObject } from 'react';
import { Link, useLocation } from 'react-router-dom';

const useClickOutside = (ref: RefObject<HTMLElement>, handler: (event: MouseEvent | TouchEvent) => void) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) return;
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

type DropdownMenu = 'nurses' | 'facilities';

type DropdownState = {
  nurses: boolean;
  facilities: boolean;
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<DropdownState>({ nurses: false, facilities: false });
  const nursesRef = useRef<HTMLDivElement>(null);
  const facilitiesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeDropdown = (menu: DropdownMenu) => setDropdownOpen(prev => ({ ...prev, [menu]: false }));
  const isActive = (path: string): boolean => location.pathname === path;

  useClickOutside(nursesRef, () => closeDropdown('nurses'));
  useClickOutside(facilitiesRef, () => closeDropdown('facilities'));

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDropdownOpen({ nurses: false, facilities: false });
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-primary to-primaryDark backdrop-blur-md border-b border-iconFourth/20 shadow-soft">
      <div className="h-20 w-full flex items-center justify-between pl-6">
        {/* Logo Section */}
        <div className="flex items-center gap-4 ml-0 pl-0">
          <Link to="/" className="flex items-center gap-4 group">
            <div className="relative">
              <img 
                src="/assets/images/vital-hive-logo.png" 
                alt="VitalHive Logo" 
                className="w-12 h-12 transition-all duration-500 group-hover:scale-110"
              />
            </div>
            <div className="flex flex-col">
              <div className="text-2xl font-extrabold bg-gradient-to-r from-secondary via-yellow100 to-secondaryDark bg-clip-text text-transparent">
                VitalHive
              </div>
              <div className="text-sm text-primaryLight font-medium tracking-wide">
                Healthcare Staffing
              </div>
            </div>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-12 ml-4 mr-8">
          {/* Nurses Dropdown */}
          <div className="relative" ref={nursesRef}>
            <button
              className={`text-iconInverted font-semibold text-lg transition-all duration-300 hover:text-secondary focus:outline-none focus:ring-0 flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-primaryLight/10 ${dropdownOpen.nurses ? 'text-secondary bg-secondaryLight/10' : ''}`}
              onClick={() => {
                setDropdownOpen({ nurses: !dropdownOpen.nurses, facilities: false });
              }}
              aria-expanded={dropdownOpen.nurses}
              aria-haspopup="true"
            >
              Nurses
              <i className={`fas fa-chevron-down text-xs transition-transform duration-300 ${dropdownOpen.nurses ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen.nurses && (
              <div className="absolute top-full left-0 mt-3 bg-card1 rounded-2xl shadow-strong border border-iconFourth/20 min-w-80 py-6 z-50 animate-fade-in">
                <div className="px-6 py-3 border-b border-iconFourth/10">
                  <div className="text-lg font-bold text-conPrimary">For Healthcare Professionals</div>
                  <div className="text-sm text-iconTritary">Find your next opportunity</div>
                </div>
                <div className="p-2">
                  <a href="https://app.vitalhivetech.com" target="_blank" rel="noopener noreferrer" className="block px-4 py-4 text-conPrimary hover:bg-secondaryLight hover:text-secondary transition-all duration-300 rounded-xl group/item">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue500/10 rounded-lg flex items-center justify-center">
                        <i className="fas fa-user-nurse text-blue500 text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold group-hover/item:text-secondary transition-colors">VitalHive For Nurses</div>
                        <div className="text-sm text-iconTritary">Join our platform</div>
                      </div>
                    </div>
                  </a>
                  <a href="https://app.vitalhivetech.com" target="_blank" rel="noopener noreferrer" className="block px-4 py-4 text-conPrimary hover:bg-secondaryLight hover:text-secondary transition-all duration-300 rounded-xl group/item">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green600/10 rounded-lg flex items-center justify-center">
                        <i className="fas fa-certificate text-green600 text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold group-hover/item:text-secondary transition-colors">STNA</div>
                        <div className="text-sm text-iconTritary">State Tested Nursing Assistant</div>
                      </div>
                    </div>
                  </a>
                  <a href="https://app.vitalhivetech.com" target="_blank" rel="noopener noreferrer" className="block px-4 py-4 text-conPrimary hover:bg-secondaryLight hover:text-secondary transition-all duration-300 rounded-xl group/item">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue600/10 rounded-lg flex items-center justify-center">
                        <i className="fas fa-stethoscope text-blue600 text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold group-hover/item:text-secondary transition-colors">LPN</div>
                        <div className="text-sm text-iconTritary">Licensed Practical Nurse</div>
                      </div>
                    </div>
                  </a>
                  <a href="https://app.vitalhivetech.com" target="_blank" rel="noopener noreferrer" className="block px-4 py-4 text-conPrimary hover:bg-secondaryLight hover:text-secondary transition-all duration-300 rounded-xl group/item">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue700/10 rounded-lg flex items-center justify-center">
                        <i className="fas fa-user-md text-blue700 text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold group-hover/item:text-secondary transition-colors">RN</div>
                        <div className="text-sm text-iconTritary">Registered Nurse</div>
                      </div>
                    </div>
                  </a>
                </div>
                <div className="px-6 py-3 border-t border-iconFourth/10 bg-secondaryLight/5">
                  <a href="https://app.vitalhivetech.com" target="_blank" rel="noopener noreferrer" className="block px-4 py-3 text-conPrimary hover:bg-secondaryLight hover:text-secondary transition-all duration-300 rounded-xl group/item">
                    <div className="flex items-center gap-3">
                      <i className="fas fa-paper-plane text-orange500"></i>
                      <span className="font-semibold group-hover/item:text-secondary transition-colors">Apply Now</span>
                    </div>
                  </a>
                  <a href="" target="_blank" rel="noopener noreferrer" className="block px-4 py-3 text-conPrimary hover:bg-secondaryLight hover:text-secondary transition-all duration-300 rounded-xl group/item">
                    <div className="flex items-center gap-3">
                      <i className="fas fa-gift text-badgePrimary"></i>
                      <span className="font-semibold group-hover/item:text-secondary transition-colors">Benefits & Perks</span>
                    </div>
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Facilities Dropdown */}
          <div className="relative" ref={facilitiesRef}>
            <button
              className={`text-iconInverted font-semibold text-lg transition-all duration-300 hover:text-secondary focus:outline-none focus:ring-0 flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-primaryLight/10 ${dropdownOpen.facilities ? 'text-secondary bg-secondaryLight/10' : ''}`}
              onClick={() => {
                setDropdownOpen({ nurses: false, facilities: !dropdownOpen.facilities });
              }}
              aria-expanded={dropdownOpen.facilities}
              aria-haspopup="true"
            >
              Facilities
              <i className={`fas fa-chevron-down text-xs transition-transform duration-300 ${dropdownOpen.facilities ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen.facilities && (
              <div className="absolute top-full left-0 mt-3 bg-card1 rounded-2xl shadow-strong border border-iconFourth/20 min-w-80 py-6 z-50 animate-fade-in">
                <div className="px-6 py-3 border-b border-iconFourth/10">
                  <div className="text-lg font-bold text-conPrimary">For Healthcare Facilities</div>
                  <div className="text-sm text-iconTritary">Find qualified staff</div>
                </div>
                <div className="p-2">
                  <a href="https://app.vitalhivetech.com/" target="_blank" rel="noopener noreferrer" className="block px-4 py-4 text-conPrimary hover:bg-secondaryLight hover:text-secondary transition-all duration-300 rounded-xl group/item">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue500/10 rounded-lg flex items-center justify-center">
                        <i className="fas fa-search text-blue500 text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold group-hover/item:text-secondary transition-colors">Find Staff</div>
                        <div className="text-sm text-iconTritary">Browse qualified candidates</div>
                      </div>
                    </div>
                  </a>
                  <a href="https://app.vitalhivetech.com/facility-shifts/" target="_blank" rel="noopener noreferrer" className="block px-4 py-4 text-conPrimary hover:bg-secondaryLight hover:text-secondary transition-all duration-300 rounded-xl group/item">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green600/10 rounded-lg flex items-center justify-center">
                        <i className="fas fa-hospital text-green600 text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold group-hover/item:text-secondary transition-colors">VitalHive for Facilities</div>
                        <div className="text-sm text-iconTritary">Manage your staffing needs</div>
                      </div>
                    </div>
                  </a>
                  <a href="https://app.vitalhivetech.com/facility-info/" target="_blank" rel="noopener noreferrer" className="block px-4 py-4 text-conPrimary hover:bg-secondaryLight hover:text-secondary transition-all duration-300 rounded-xl group/item">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue600/10 rounded-lg flex items-center justify-center">
                        <i className="fas fa-info-circle text-blue600 text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold group-hover/item:text-secondary transition-colors">Facility Info</div>
                        <div className="text-sm text-iconTritary">Learn about our services</div>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Links */}
          <Link to="/products" className={`text-iconInverted font-semibold text-lg transition-all duration-300 hover:text-secondary focus:outline-none focus:ring-0 px-3 py-2 rounded-lg hover:bg-primaryLight/10 ${isActive('/products') ? 'text-secondary bg-secondaryLight/10' : ''}`}>
            Products
          </Link>
          <Link to="/about" className={`text-iconInverted font-semibold text-lg transition-all duration-300 hover:text-secondary focus:outline-none focus:ring-0 px-3 py-2 rounded-lg hover:bg-primaryLight/10 ${isActive('/about') ? 'text-secondary bg-secondaryLight/10' : ''}`}>
            About
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button 
          onClick={toggleMenu} 
          className="lg:hidden flex flex-col gap-1.5 p-2 rounded-lg hover:bg-primaryLight/10 transition-all duration-300" 
          aria-label="Toggle navigation menu" 
          aria-expanded={isMenuOpen}
        >
          <span className={`block w-6 h-0.5 bg-secondary rounded transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
          <span className={`block w-6 h-0.5 bg-secondary rounded transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
          <span className={`block w-6 h-0.5 bg-secondary rounded transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden absolute top-full left-0 right-0 bg-gradient-to-b from-primaryDark to-primary border-t border-iconFourth/20 transform transition-all duration-500 ${isMenuOpen ? 'translate-y-0 opacity-100 visible' : '-translate-y-full opacity-0 invisible'}`}>
        <div className="px-6 py-4 space-y-2">
          <Link to="/nurses" className="block px-4 py-3 text-secondary font-semibold rounded-xl hover:bg-secondaryLight/10 transition-all duration-300" onClick={toggleMenu}>
            <div className="flex items-center gap-3">
              <i className="fas fa-user-nurse text-blue500"></i>
              <span>Nurses</span>
            </div>
          </Link>
          <Link to="/facilities" className="block px-4 py-3 text-secondary font-semibold rounded-xl hover:bg-secondaryLight/10 transition-all duration-300" onClick={toggleMenu}>
            <div className="flex items-center gap-3">
              <i className="fas fa-hospital text-green600"></i>
              <span>Facilities</span>
            </div>
          </Link>
          <Link to="/products" className="block px-4 py-3 text-secondary font-semibold rounded-xl hover:bg-secondaryLight/10 transition-all duration-300" onClick={toggleMenu}>
            <div className="flex items-center gap-3">
              <i className="fas fa-cube text-orange500"></i>
              <span>Products</span>
            </div>
          </Link>
          <Link to="/about" className="block px-4 py-3 text-secondary font-semibold rounded-xl hover:bg-secondaryLight/10 transition-all duration-300" onClick={toggleMenu}>
            <div className="flex items-center gap-3">
              <i className="fas fa-info-circle text-blue600"></i>
              <span>About</span>
            </div>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 